import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";
import {
  MachinesSorting,
  machinesColumnsData,
  sortDirections,
} from "shared/constants";
import { MachineItem, ProcessTemplateItem } from "shared/api";
import { TableButton, TableSearchFilter } from "shared/ui";
import { FilterFilled } from "@ant-design/icons";
import classNames from "classnames";
import { Select } from "antd";

interface UseGetMachinesTableColumnsProps {
  t: TFunction;
  editMachinesHandler: (record: MachineItem) => void;
  showMachineDeleteModal: (record: MachineItem) => void;
  changeSort: (ascendOrdering: string, descendOrdering: string) => void;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  handleMachineNameValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  machineNameValue: string;
  clearMachineNameValue: () => void;
  processTemplates: ProcessTemplateItem[];
  selectedTemplates: number[];
  handleTemplateFilterChange: (values: number[]) => void;
}

const { name, processTemplate, actions } = machinesColumnsData;

export const useGetMachinesTableColumns = ({
  t,
  editMachinesHandler,
  showMachineDeleteModal,
  changeSort,
  hasDeletePermission,
  hasEditPermission,
  handleMachineNameValue,
  machineNameValue,
  clearMachineNameValue,
  handleTemplateFilterChange,
  processTemplates,
  selectedTemplates,
}: UseGetMachinesTableColumnsProps): ColumnsType<MachineItem> => {
  const columns: ColumnsType<MachineItem> = [
    {
      title: t(name.title),
      key: "name",
      width: name.width,
      sorter: true,
      sortDirections,
      onHeaderCell: () => ({
        onClick: () => {
          changeSort(MachinesSorting.NAME_ASC, MachinesSorting.NAME_DESC);
        },
      }),
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600  scale-125": machineNameValue.length > 0,
          })}
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={clearMachineNameValue}
          confirm={confirm}
          value={machineNameValue}
          handleFilterChange={handleMachineNameValue}
          visible={visible}
          placeholder={t("Machine name")!}
          title={t("Filter by machine")!}
        />
      ),
      render: (machine: MachineItem) => (
        <TableButton
          type="title"
          title={machine.name}
          onClick={
            hasEditPermission ? () => editMachinesHandler(machine) : undefined
          }
        />
      ),
    },

    {
      title: t(processTemplate.title),
      key: processTemplate.key,
      width: processTemplate.width,
      sorter: true,
      sortDirections,
      filterIcon: (
        <FilterFilled
          className={classNames("", {
            "!text-blue-600 scale-125": selectedTemplates.length > 0,
          })}
        />
      ),
      filterDropdown: () => (
        <div className="p-3 w-60">
          <div className="font-medium mb-2">
            {t("machines.filter-by-template")}
          </div>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder={t("machines.select-template")}
            value={selectedTemplates}
            onChange={handleTemplateFilterChange}
            options={processTemplates.map((tpl) => ({
              value: tpl.id,
              label: tpl.name,
            }))}
          />
        </div>
      ),
      render: (item: MachineItem) => item?.process_template?.name || "-",
    },
  ];

  if (hasDeletePermission) {
    columns.push({
      title: t(actions.title),
      key: actions.key,
      width: actions.width,

      render: (record: MachineItem) => {
        return (
          <div className="w-full flex items-center justify-around">
            <TableButton
              type="delete"
              onClick={() => showMachineDeleteModal(record)}
            />
          </div>
        );
      },
    });
  }

  return columns;
};
